import { ApiError } from './errors/ApiError'

export class ApiClient {
  private baseUrl: string
  private token: string | null = null

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || import.meta.env.VITE_API_BASE_URL || 'https://api.phisheez.net'
  }

  // Set authentication token
  setToken(token: string | null): void {
    this.token = token
  }

  // Get current token (for debugging)
  getToken(): string | null {
    return this.token
  }

  // Generic request method
  async request<T = unknown>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const fullUrl = `${this.baseUrl}${endpoint}`
    
    // Build headers object properly
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    }

    // Add authorization header if token available
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`
      console.log(`[ApiClient] Request to ${endpoint} with auth token:`, this.token.substring(0, 20) + '...')
    } else {
      console.log(`[ApiClient] Request to ${endpoint} WITHOUT auth token`)
    }

    const config: RequestInit = {
      ...options,
      headers,
    }

    try {
      const response = await fetch(fullUrl, config)
      
      // Handle different response types
      const contentType = response.headers.get('content-type')
      let data: unknown
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json()
      } else {
        data = await response.text()
      }

      if (!response.ok) {
        console.error(`[ApiClient] Request failed: ${response.status}`, data)
        throw new ApiError(response.status, data as any, response)
      }

      return data as T
    } catch (error) {
      if (error instanceof ApiError) {
        throw error
      }
      
      // Handle network errors
      throw new ApiError(0, {
        code: 'NETWORK_ERROR',
        message: 'Unable to connect to server'
      })
    }
  }

  // GET request
  async get<T = unknown>(endpoint: string, params: Record<string, string | number | boolean> = {}): Promise<T> {
    const queryString = new URLSearchParams(
      Object.entries(params).map(([key, value]) => [key, String(value)])
    ).toString()
    const url = queryString ? `${endpoint}?${queryString}` : endpoint
    
    return this.request<T>(url, { method: 'GET' })
  }

  // POST request
  async post<T = unknown>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  // PUT request
  async put<T = unknown>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  // PATCH request
  async patch<T = unknown>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  // DELETE request
  async delete<T = unknown>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' })
  }
}
